import { Section } from '@/types';

type ParentId = string | undefined;

const toParentId = (value?: string | null): ParentId => (value == null ? undefined : value);
const parentKey = (value?: string | null): string => value ?? '__ROOT__';

const sortByOrder = (a: Section, b: Section) => a.order - b.order;

export type OutlineReorderAction =
  | { type: 'move-up'; sectionId: string }
  | { type: 'move-down'; sectionId: string }
  | { type: 'indent'; sectionId: string }
  | { type: 'outdent'; sectionId: string };

export interface OutlineReorderUpdate {
  id: string;
  changes: {
    parentSectionId?: string;
    order: number;
    level: number;
  };
}

export interface OutlineReorderResult {
  changed: boolean;
  updates: OutlineReorderUpdate[];
}

interface DraftState {
  parentSectionId?: string;
  order: number;
  level: number;
}

function buildChildrenMap(sections: Section[]): Map<string, Section[]> {
  const map = new Map<string, Section[]>();
  for (const section of sections) {
    const key = parentKey(section.parentSectionId);
    const arr = map.get(key) ?? [];
    arr.push(section);
    map.set(key, arr);
  }
  for (const arr of map.values()) {
    arr.sort(sortByOrder);
  }
  return map;
}

function collectDescendantIds(sectionId: string, sectionsById: Map<string, Section>): string[] {
  const map = new Map<string, string[]>();
  for (const section of sectionsById.values()) {
    if (!section.parentSectionId) continue;
    const arr = map.get(section.parentSectionId) ?? [];
    arr.push(section.id);
    map.set(section.parentSectionId, arr);
  }

  const ids: string[] = [];
  const stack = [...(map.get(sectionId) ?? [])];
  while (stack.length) {
    const current = stack.pop()!;
    ids.push(current);
    const next = map.get(current);
    if (next) stack.push(...next);
  }
  return ids;
}

function applySiblingOrder(
  ids: string[],
  parentSectionId: ParentId,
  draft: Map<string, DraftState>
) {
  ids.forEach((id, index) => {
    const target = draft.get(id);
    if (!target) return;
    target.parentSectionId = parentSectionId;
    target.order = index;
  });
}

function makeResult(
  original: Map<string, Section>,
  draft: Map<string, DraftState>
): OutlineReorderResult {
  const updates: OutlineReorderUpdate[] = [];
  for (const [id, next] of draft.entries()) {
    const prev = original.get(id);
    if (!prev) continue;
    const prevParent = toParentId(prev.parentSectionId);
    const nextParent = toParentId(next.parentSectionId);
    if (prevParent === nextParent && prev.order === next.order && prev.level === next.level) {
      continue;
    }
    updates.push({
      id,
      changes: {
        parentSectionId: nextParent,
        order: next.order,
        level: next.level,
      },
    });
  }
  return { changed: updates.length > 0, updates };
}

export function computeOutlineReorder(
  sections: Section[],
  action: OutlineReorderAction
): OutlineReorderResult {
  const byId = new Map(sections.map((section) => [section.id, section] as const));
  const source = byId.get(action.sectionId);
  if (!source) return { changed: false, updates: [] };

  const siblingsByParent = buildChildrenMap(sections);
  const draft = new Map<string, DraftState>(
    sections.map((section) => [
      section.id,
      {
        parentSectionId: toParentId(section.parentSectionId),
        order: section.order,
        level: section.level,
      },
    ])
  );

  const sourceParent = toParentId(source.parentSectionId);
  const sourceSiblings = [...(siblingsByParent.get(parentKey(sourceParent)) ?? [])];
  const sourceIndex = sourceSiblings.findIndex((section) => section.id === source.id);
  if (sourceIndex < 0) return { changed: false, updates: [] };

  if (action.type === 'move-up' || action.type === 'move-down') {
    const nextIndex = action.type === 'move-up' ? sourceIndex - 1 : sourceIndex + 1;
    if (nextIndex < 0 || nextIndex >= sourceSiblings.length) {
      return { changed: false, updates: [] };
    }
    const reordered = sourceSiblings.map((section) => section.id);
    [reordered[sourceIndex], reordered[nextIndex]] = [reordered[nextIndex], reordered[sourceIndex]];
    applySiblingOrder(reordered, sourceParent, draft);
    return makeResult(byId, draft);
  }

  if (action.type === 'indent') {
    if (sourceIndex === 0) return { changed: false, updates: [] };
    const previousSibling = sourceSiblings[sourceIndex - 1];
    if (!previousSibling) return { changed: false, updates: [] };

    const oldSiblings = sourceSiblings.filter((section) => section.id !== source.id).map((section) => section.id);
    applySiblingOrder(oldSiblings, sourceParent, draft);

    const newParentId = previousSibling.id;
    const newParent = byId.get(newParentId);
    if (!newParent) return { changed: false, updates: [] };

    const newChildren = [...(siblingsByParent.get(parentKey(newParentId)) ?? [])]
      .filter((section) => section.id !== source.id)
      .map((section) => section.id);
    newChildren.push(source.id);
    applySiblingOrder(newChildren, newParentId, draft);

    const newLevel = (newParent.level ?? 0) + 1;
    const sourceDraft = draft.get(source.id);
    if (sourceDraft) {
      sourceDraft.level = newLevel;
    }

    const levelDelta = newLevel - source.level;
    if (levelDelta !== 0) {
      const descendants = collectDescendantIds(source.id, byId);
      for (const descendantId of descendants) {
        const descendant = byId.get(descendantId);
        const target = draft.get(descendantId);
        if (!descendant || !target) continue;
        target.level = Math.max(0, descendant.level + levelDelta);
      }
    }

    return makeResult(byId, draft);
  }

  const parentId = toParentId(source.parentSectionId);
  if (!parentId) return { changed: false, updates: [] };
  const parent = byId.get(parentId);
  if (!parent) return { changed: false, updates: [] };

  const grandParentId = toParentId(parent.parentSectionId);
  const oldChildren = [...(siblingsByParent.get(parentKey(parentId)) ?? [])];
  const oldChildrenWithoutSource = oldChildren
    .filter((section) => section.id !== source.id)
    .map((section) => section.id);
  applySiblingOrder(oldChildrenWithoutSource, parentId, draft);

  const nextSiblings = [...(siblingsByParent.get(parentKey(grandParentId)) ?? [])]
    .filter((section) => section.id !== source.id)
    .map((section) => section.id);
  const parentIndex = nextSiblings.indexOf(parent.id);
  if (parentIndex < 0) return { changed: false, updates: [] };
  nextSiblings.splice(parentIndex + 1, 0, source.id);
  applySiblingOrder(nextSiblings, grandParentId, draft);

  const newLevel = parent.level ?? 0;
  const sourceDraft = draft.get(source.id);
  if (sourceDraft) {
    sourceDraft.level = newLevel;
  }

  const levelDelta = newLevel - source.level;
  if (levelDelta !== 0) {
    const descendants = collectDescendantIds(source.id, byId);
    for (const descendantId of descendants) {
      const descendant = byId.get(descendantId);
      const target = draft.get(descendantId);
      if (!descendant || !target) continue;
      target.level = Math.max(0, descendant.level + levelDelta);
    }
  }

  return makeResult(byId, draft);
}
